import { Component, OnDestroy, OnInit, ViewEncapsulation, EventEmitter, ComponentFactoryResolver } from '@angular/core';
import { CIJobTypesGridColumnsModel } from './model/ci-job-types-grid-columns.model';
import { CIJobTypesService } from './service/ci-job-types.service';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { BroadcastEventModel, BroadcastService, GridBaseComponent, ModalService, ModalSize, ToasterType } from '@cv-invision/core';
import { CustomActionCIJobTypesCellComponent } from './custom-grid-cell/custom-action-ci-job-types-cell.component';
import { DELETE_CI_JOB_TYPE, ADD_EDIT_CI_JOB_TYPE, UPDATE_CI_JOB_TYPES_LIST } from './model/ci-job-types.model';
import { CreateUpdateCIJobTypeComponent } from './create-update-ci-job-type/create-update-ci-job-type.component';

@Component({
	selector: 'cv-invision-ci-job-types',
	encapsulation: ViewEncapsulation.None,
	templateUrl: './ci-job-types.component.html',
	styleUrls: ['./ci-job-types.component.scss']
})
export class CIJobTypesComponent extends GridBaseComponent implements OnInit, OnDestroy {
	protected config: Array<any>;
	protected tmpGridData: Array<any>;
	private atComponentDestroy = new Subject();
	private searchTextChange: EventEmitter<any> = new EventEmitter<any>();
	DEFAULT_ROW_HEIGHT = 40;
	public searchText: string;

	constructor(
		private broadcastService: BroadcastService,
		private ciJobTypesService: CIJobTypesService,
		private modalService: ModalService,
		private componentFactoryResolver: ComponentFactoryResolver,
	) {
		super();
	}

	ngOnInit(): void {
		this.gridOptions.columnDefs = new CIJobTypesGridColumnsModel()
			.getColumns();

		this.gridOptions.frameworkComponents = {
			customActionCIJobTypesCellComponent: CustomActionCIJobTypesCellComponent,
		};

		this.searchTextChange
			.pipe(
				distinctUntilChanged()
			)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((event: any) => {
				// this.searchText = event.value.replace(/[&/\\#,+()$~%'":*?<>{}]/g, '') || '';
				this.searchText = event.value ? event.value : '' ;
				this.applyFilter();
				this.gridHeight = this.getGridHeight(this.gridData.length > 0 ? this.gridData.length : 0);
			});

		this.loadGridData();
		this.registerRefreshListListener();
		this.registerDeleteCIJobTypeListener();
		this.registerAddEditCIJobTypeListener();

		this.ciJobTypesService
			.getReloadListConfirmationListener()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				if (result && result.reloadList) {
					this.loadGridData();
				}
			});
	}

	/***
	 * Add CI Job Type
	 */
	onAddCIJobType(): void {
		this.modalService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: CreateUpdateCIJobTypeComponent,
				data: {},
				modalConfiguration: {
					title: 'New CI Product Configuration',
					modalSize: ModalSize.XL
				}
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				if (result.success) {
					this.loadGridData();
				} else {
					// it failed :(
				}
			});
	}

	/**
	 * Register Listener to Delete a CI Job Type
	 */
	private registerDeleteCIJobTypeListener(): void {
		this.broadcastService.on(DELETE_CI_JOB_TYPE, (broadcast: BroadcastEventModel) => {

			const newCIJobTypesConfiguration = this.config.filter((CIJobType, index) => {
				return broadcast.event.index !== index;
			});
			this.config = newCIJobTypesConfiguration;
			this.onSave();
		});
	}

	/**
	 * Register Listener for Add Edit CI Job Type
	 */
	private registerAddEditCIJobTypeListener(): void {
		this.broadcastService.on(ADD_EDIT_CI_JOB_TYPE, (broadcast: BroadcastEventModel) => {

			if (broadcast.event.isEditing) {
				this.config[broadcast.event.index] = broadcast.event.data;
			} else {
				this.config.push(broadcast.event.data);
			}

			this.onSave();
		});
	}

	/**
	 * Save current configuration.
	 */
	onSave(): void {
		this.ciJobTypesService
			.saveAppScopeCIJobTypesConfiguration(this.config)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				this.loadGridData();
				if (result && result.succeeded) {
					this.broadcastService.broadcast({
						name: ToasterType.SUCCESS,
						event: { message: 'Changes saved successfully' }
					});
				}
			}, error => {
				console.error(error);
				this.broadcastService.broadcast({
					name: ToasterType.ERROR,
					event: { error: `Error occurred ${error}` }
				});
			});
	}

	/**
	 * Load grid data implementation to load the CI Job Types.
	 */
	loadGridData(): void {
		this.ciJobTypesService
			.getAppScopeCIJobTypesConfiguration()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {

				this.config = result;
				this.config = this.config.map((ciJobType, key) => {
					return {
						id: (key),
						...ciJobType
					};
				});

				this.gridData = this.config;

				this.tmpGridData = [...this.config];

				if (this.searchText) {
					this.applyFilter();
				}

				this.gridHeight = this.getGridHeight(this.gridData.length > 0 ? this.gridData.length : 0);
			});
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/**
	 * Register Lister to refresh mapper ci job types list
	 */
	private registerRefreshListListener(): void {
		this.broadcastService
			.onEvent(UPDATE_CI_JOB_TYPES_LIST)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((e: BroadcastEventModel) => {
				this.loadGridData();
			});
	}

	/**
	 * Search
	 */
	onSearchTextChange(event: any): void {
		this.searchTextChange.emit({ value: event.value });
	}

	/**
	 * Filter
	 * @param offset
	 */
	applyFilter(): void {
		const result = this.tmpGridData.filter(product => {
			return product.origin.toLowerCase().indexOf(this.searchText.toLowerCase()) !== -1 ||
				product.formName.toLowerCase().indexOf(this.searchText.toLowerCase()) !== -1 ||
				product.productName.toLowerCase().indexOf(this.searchText.toLowerCase()) !== -1;
		});

		this.gridData = result;
	}
}
